import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from './ui/card';
import { cn } from '../lib/utils';

const StatsCard = ({ title, value, icon: Icon, trend, trendUp, isNegative, className = "" }) => {
  const { language, t } = useLanguage();
  // Wir schauen uns die Übersetzungsschlüssel oder Titel an, um zu entscheiden, ob es ein Geldwert ist.
  const lowerTitle = title?.toLowerCase() || '';
  const isCurrency = lowerTitle.includes('€') || 
                     lowerTitle.includes('balance') || 
                     lowerTitle.includes('guthaben') || 
                     lowerTitle.includes('payout') || 
                     lowerTitle.includes('gewinn') ||
                     lowerTitle.includes('gewonnen') ||
                     lowerTitle.includes('verloren') ||
                     lowerTitle.includes('lost') ||
                     lowerTitle.includes('won') ||
                     lowerTitle.includes('offen') ||
                     lowerTitle.includes('wetteinsatz') ||
                     lowerTitle.includes('verwettet') ||
                     lowerTitle.includes('wager') ||
                     lowerTitle.includes('einsatz') ||
                     lowerTitle.includes('liquidity') ||
                     lowerTitle.includes('liquidität') ||
                     lowerTitle.includes('profit') ||
                     lowerTitle.includes('loss') ||
                     lowerTitle.includes('verlust');

  const isNetResult = title === t('netProfitLoss') || lowerTitle.includes('gewinn / verlust') || lowerTitle.includes('profit / loss');
  const numericValue = typeof value === "number" ? value : parseFloat(value);
  const hasNumericValue = !isNaN(numericValue);
  const isPositive = hasNumericValue && numericValue >= 0;

  const displayValue = typeof value === "number" 
    ? Math.abs(value).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { 
        minimumFractionDigits: isCurrency ? 2 : 0, 
        maximumFractionDigits: 2 
      }) + (isCurrency ? "\u00A0€" : "")
    : value;

  const isRed = isNetResult ? !isPositive : (isNegative || (hasNumericValue && numericValue < 0));
  const isGreen = isNetResult && isPositive;

  let prefix = "";
  if (isNetResult) {
    prefix = isPositive ? "+" : "−";
  } else if (!isNetResult && (isNegative || (hasNumericValue && numericValue < 0))) {
    prefix = "−";
  }

  let valueColorClass = "";
  if (isGreen) {
    valueColorClass = "text-green-500 dark:text-green-400";
  } else if (isRed) {
    valueColorClass = "text-red-500 dark:text-red-400";
  }

  const isWon = lowerTitle.includes('gewonnen') || lowerTitle.includes('won');
  const isLost = lowerTitle.includes('verloren') || lowerTitle.includes('lost');
  const isActiveWager = lowerTitle.includes('offener einsatz') || lowerTitle.includes('active wager') || lowerTitle.includes('wager');
  const isTotalBets = lowerTitle.includes('gesamt wetten') || lowerTitle.includes('total bets') || lowerTitle.includes('bets');
  const isWinRate = lowerTitle.includes('gewinnrate') || lowerTitle.includes('win rate');

  let iconBgClass = "bg-secondary/40 text-muted-foreground";
  let cardGlowClass = "from-transparent to-transparent";

  if (isNetResult) {
    iconBgClass = isPositive ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400";
    cardGlowClass = isPositive ? "group-hover:from-green-500/5 group-hover:to-transparent" : "group-hover:from-red-500/5 group-hover:to-transparent";
  } else if (isWon) {
    iconBgClass = "bg-emerald-500/10 text-emerald-400";
    cardGlowClass = "group-hover:from-emerald-500/5 group-hover:to-transparent";
  } else if (isLost) {
    iconBgClass = "bg-red-500/10 text-red-400";
    cardGlowClass = "group-hover:from-red-500/5 group-hover:to-transparent";
  } else if (isActiveWager) {
    iconBgClass = "bg-blue-500/10 text-blue-400";
    cardGlowClass = "group-hover:from-blue-500/5 group-hover:to-transparent";
  } else if (isTotalBets) {
    iconBgClass = "bg-violet-500/10 text-violet-400";
    cardGlowClass = "group-hover:from-violet-500/5 group-hover:to-transparent";
  } else if (isWinRate) {
    iconBgClass = "bg-amber-500/10 text-amber-400";
    cardGlowClass = "group-hover:from-amber-500/5 group-hover:to-transparent";
  }

  return (
    <Card
      className={cn(
        "stat-card relative overflow-hidden h-full group bg-card/60 border-border/40 backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.15)] hover:border-primary/20 hover:shadow-[0_8px_30px_rgba(0,0,0,0.25)] hover:-translate-y-0.5 transition-all duration-300",
        className
      )}
      data-testid={title ? `stats-card-${title.toLowerCase().replace(/\s+/g, "-")}` : "stats-card"}>
      {/* Dynamic Hover Radial Glow Effect */}
      <div className={cn(
        "absolute inset-0 bg-gradient-to-tr via-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-xl",
        cardGlowClass
      )} />
      <CardContent className="p-4 flex flex-col justify-between h-full w-full relative z-10">
        <div className="flex items-start justify-between gap-2 w-full">
          <p className="text-xs sm:text-sm text-muted-foreground font-medium leading-tight">{title}</p>
          {Icon && (
            <div className={cn("p-2 rounded-xl shrink-0 transition-all duration-300 group-hover:scale-110", iconBgClass)}>
              <Icon className="w-4 h-4" />
            </div>
          )}
        </div>
        <div className="mt-4 flex flex-col justify-end flex-grow w-full">
          <p className={cn("text-lg sm:text-2xl font-mono font-bold number-highlight whitespace-nowrap leading-none tracking-tight", valueColorClass)}>
            {prefix}
            {displayValue}
          </p>
          {trend && (
            <p className={cn("text-[10px] sm:text-xs mt-1.5 font-medium flex items-center gap-1", trendUp ? "text-green-400" : "text-red-400")}>
              <span>{trendUp ? "↑" : "↓"}</span> {trend}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default StatsCard;
